import { useCallback } from 'react';

/**
 * useTemplateMergeFieldDecorations
 *
 * Post-merge Apryse decorations for documents that were just created from a
 * Formstack template. Applied once per "first open after merge" (gated by the
 * `fromTemplateMerge: true` flag the direct-merge flow threads through
 * navigate() state).
 *
 * Does two things, both non-destructive to the underlying PDF:
 *
 * 1. Highlights AcroForm widget annotations whose value is empty after the
 *    merge (Salesforce didn't resolve them). Background: pale yellow
 *    (#fffbeb, matching the admin FieldMappingModal missing-field callout
 *    pattern shipped in dms_frontend PR #64). Border: amber (#f59e0b). Gives
 *    paralegals an immediate visual map of "what still needs my attention"
 *    without any out-of-PDF checklist UI.
 *
 * 2. Installs a one-shot "fieldChanged" listener that strips the highlight
 *    from a widget the first time the user edits it, so the yellow treatment
 *    reads as "needs input" rather than "was empty at some point."
 *
 * Apryse caveat: widget appearance is driven by both the annotation's
 * Fill/StrokeColor AND the underlying field's appearance stream. We set the
 * annotation color and call `drawAnnotationsFromList` to repaint. In our
 * Formstack-unflattened PDFs this is sufficient; fillable-PDF templates with
 * the "click if your fields appear blank until clicked" setting on the
 * Formstack side may also need `refreshAppearances()` — left as a future
 * knob if we see field-text invisible-on-open complaints from users.
 */
const EMPTY_FIELD_BG = [255, 251, 235];
const EMPTY_FIELD_BORDER = [245, 158, 11];

const isWidgetAnnotation = (annotation, Annotations) => {
  const WidgetCtor = Annotations?.WidgetAnnotation;
  if (WidgetCtor && annotation instanceof WidgetCtor) return true;
  if (typeof annotation?.getField === 'function') return true;
  return false;
};

const getFieldValue = (widget) => {
  try {
    const field = widget.getField?.();
    if (!field) return null;
    if (typeof field.getValue === 'function') return field.getValue();
    return field.value ?? null;
  } catch (err) {
    return null;
  }
};

const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') return value.trim() === '';
  if (Array.isArray(value)) return value.length === 0;
  return false;
};

const useTemplateMergeFieldDecorations = () => {
  const highlightEmptyMergeFields = useCallback((instance) => {
    if (!instance?.Core) return () => {};
    const { annotationManager, Annotations } = instance.Core;
    if (!annotationManager) return () => {};

    const annotations = annotationManager.getAnnotationsList?.() || [];
    const highlightedWidgets = new Set();

    const decorate = (widget) => {
      try {
        if (Annotations?.Color) {
          widget.FillColor = new Annotations.Color(
            EMPTY_FIELD_BG[0], EMPTY_FIELD_BG[1], EMPTY_FIELD_BG[2], 1
          );
          widget.StrokeColor = new Annotations.Color(
            EMPTY_FIELD_BORDER[0], EMPTY_FIELD_BORDER[1], EMPTY_FIELD_BORDER[2], 1
          );
        }
        widget.StrokeThickness = 1.5;
        widget.Author = widget.Author || 'dms-template-merge';
        widget.setCustomData?.('dms-empty-after-merge', 'true');
        highlightedWidgets.add(widget);
      } catch (err) {
        console.warn('[template-merge-decorations] Failed to decorate widget:', err);
      }
    };

    annotations.forEach((annotation) => {
      if (!isWidgetAnnotation(annotation, Annotations)) return;
      const value = getFieldValue(annotation);
      if (!isEmpty(value)) return;
      decorate(annotation);
    });

    if (highlightedWidgets.size === 0) return () => {};

    try {
      annotationManager.drawAnnotationsFromList?.(Array.from(highlightedWidgets));
    } catch (err) {
      console.warn('[template-merge-decorations] redraw failed:', err);
    }

    // One-shot fieldChanged listener: when a highlighted widget receives a
    // non-empty value (user filled it in), clear the highlight so the UX
    // communicates "addressed" rather than continuing to shout for attention.
    const fieldChangedHandler = (field, _newValue) => {
      try {
        const widgetsForField = field?.widgets || [];
        widgetsForField.forEach((widget) => {
          if (!highlightedWidgets.has(widget)) return;
          const current = getFieldValue(widget);
          if (isEmpty(current)) return;
          widget.FillColor = Annotations?.Color
            ? new Annotations.Color(255, 255, 255, 0)
            : widget.FillColor;
          widget.StrokeColor = Annotations?.Color
            ? new Annotations.Color(0, 0, 0, 1)
            : widget.StrokeColor;
          widget.StrokeThickness = 0;
          widget.setCustomData?.('dms-empty-after-merge', 'false');
          highlightedWidgets.delete(widget);
          try {
            annotationManager.drawAnnotationsFromList?.([widget]);
          } catch (_) {
            /* redraw best-effort */
          }
        });
      } catch (err) {
        console.warn('[template-merge-decorations] clear-on-fill failed:', err);
      }
    };

    annotationManager.addEventListener?.('fieldChanged', fieldChangedHandler);

    return () => {
      try {
        annotationManager.removeEventListener?.('fieldChanged', fieldChangedHandler);
      } catch (_) { /* noop */ }
    };
  }, []);

  return { highlightEmptyMergeFields };
};

export default useTemplateMergeFieldDecorations;
